import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import {
  TIMELINE_CATEGORY_DOT_COLORS,
  TIMELINE_CATEGORY_LABELS,
  TIMELINE_CATEGORY_SHORT_LABELS,
  TimelineEvent,
  dayKeyParis,
  formatTimelineDate,
  formatTimelineDateTime,
} from '../../utils/studentTimeline';

type Props = {
  events: TimelineEvent[];
  onEventClick?: (event: TimelineEvent) => void;
};

const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MAX_VISIBLE_CHIPS = 3;

const startOfMonthParis = (year: number, month: number): Date =>
  new Date(Date.UTC(year, month, 1, 12, 0, 0));

const addMonths = (date: Date, delta: number): Date =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + delta, 1, 12, 0, 0));

const monthLabel = (date: Date): string =>
  date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });

const buildMonthGrid = (anchor: Date): Array<{ date: Date | null; key: string }> => {
  const year = anchor.getUTCFullYear();
  const month = anchor.getUTCMonth();
  const first = new Date(Date.UTC(year, month, 1, 12, 0, 0));
  const firstWeekday = (first.getUTCDay() + 6) % 7;
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0, 12, 0, 0)).getUTCDate();

  const cells: Array<{ date: Date | null; key: string }> = [];
  for (let i = 0; i < firstWeekday; i += 1) {
    cells.push({ date: null, key: `empty-start-${i}` });
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      date: new Date(Date.UTC(year, month, day, 12, 0, 0)),
      key: `day-${day}`,
    });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ date: null, key: `empty-end-${cells.length}` });
  }
  return cells;
};

const StudentParcoursCalendar: React.FC<Props> = ({ events, onEventClick }) => {
  const [anchor, setAnchor] = useState(() =>
    startOfMonthParis(new Date().getFullYear(), new Date().getMonth())
  );
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, TimelineEvent[]>();
    events.forEach((event) => {
      const key = dayKeyParis(event.startAt);
      const list = map.get(key) || [];
      list.push(event);
      map.set(key, list);
    });
    return map;
  }, [events]);

  const grid = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const todayKey = dayKeyParis(new Date().toISOString());

  const selectedEvents = selectedDay
    ? [...(eventsByDay.get(selectedDay) || [])].sort(
        (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
      )
    : [];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <IconButton onClick={() => setAnchor((prev) => addMonths(prev, -1))} aria-label="Mois précédent">
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
          {monthLabel(anchor)}
        </Typography>
        <IconButton onClick={() => setAnchor((prev) => addMonths(prev, 1))} aria-label="Mois suivant">
          <ChevronRightIcon />
        </IconButton>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: 1,
          mb: 2,
        }}
      >
        {WEEKDAY_LABELS.map((label) => (
          <Typography key={label} variant="caption" color="text.secondary" textAlign="center" fontWeight={600}>
            {label}
          </Typography>
        ))}
        {grid.map((cell) => {
          if (!cell.date) {
            return <Box key={cell.key} sx={{ minHeight: 120 }} />;
          }

          const dayKey = dayKeyParis(cell.date.toISOString());
          const dayEvents = eventsByDay.get(dayKey) || [];
          const isToday = dayKey === todayKey;
          const isSelected = selectedDay === dayKey;
          const hiddenCount = Math.max(0, dayEvents.length - MAX_VISIBLE_CHIPS);

          return (
            <Paper
              key={cell.key}
              variant="outlined"
              onClick={() => setSelectedDay(dayKey)}
              sx={{
                minHeight: 120,
                p: 0.75,
                cursor: 'pointer',
                borderWidth: isToday ? 2 : 1,
                borderColor: isSelected
                  ? 'primary.dark'
                  : isToday
                  ? 'primary.main'
                  : 'divider',
                bgcolor: isSelected
                  ? 'action.selected'
                  : isToday
                  ? (theme) => alpha(theme.palette.primary.main, 0.1)
                  : 'background.paper',
                boxShadow: isToday ? '0 0 0 1px rgba(25, 118, 210, 0.2)' : 'none',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                ...(isToday && {
                  '&::before': {
                    content: '"Aujourd\'hui"',
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    fontSize: '0.58rem',
                    fontWeight: 700,
                    color: 'primary.main',
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                    lineHeight: 1,
                  },
                }),
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'flex-start',
                  minWidth: isToday ? 28 : 'auto',
                  height: isToday ? 28 : 'auto',
                  px: isToday ? 0 : 0,
                  mb: 0.5,
                  borderRadius: isToday ? '50%' : 0,
                  bgcolor: isToday ? 'primary.main' : 'transparent',
                  color: isToday ? 'primary.contrastText' : 'text.primary',
                }}
              >
                <Typography variant="body2" fontWeight={isToday ? 800 : 500} lineHeight={1}>
                  {cell.date.getUTCDate()}
                </Typography>
              </Box>
              <Stack spacing={0.5} sx={{ flex: 1, overflow: 'hidden' }}>
                {dayEvents.slice(0, MAX_VISIBLE_CHIPS).map((event) => (
                  <Chip
                    key={event.id}
                    size="small"
                    label={TIMELINE_CATEGORY_SHORT_LABELS[event.category]}
                    sx={{
                      height: 22,
                      maxWidth: '100%',
                      bgcolor: TIMELINE_CATEGORY_DOT_COLORS[event.category],
                      color: '#fff',
                      borderStyle: event.isFuture ? 'dashed' : 'solid',
                      borderWidth: event.isFuture ? 1 : 0,
                      borderColor: 'rgba(255,255,255,0.8)',
                      '& .MuiChip-label': {
                        px: 0.75,
                        fontSize: '0.68rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                ))}
                {hiddenCount > 0 && (
                  <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                    +{hiddenCount} autre{hiddenCount > 1 ? 's' : ''}
                  </Typography>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
        {(Object.keys(TIMELINE_CATEGORY_LABELS) as Array<keyof typeof TIMELINE_CATEGORY_LABELS>).map(
          (category) => (
            <Chip
              key={category}
              size="small"
              label={TIMELINE_CATEGORY_SHORT_LABELS[category]}
              sx={{
                bgcolor: TIMELINE_CATEGORY_DOT_COLORS[category],
                color: '#fff',
              }}
            />
          )
        )}
      </Stack>

      {selectedDay && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            {formatTimelineDate(`${selectedDay}T12:00:00.000Z`)}
          </Typography>
          {selectedEvents.length === 0 ? (
            <Typography color="text.secondary">Aucun événement ce jour-là.</Typography>
          ) : (
            <Stack spacing={1.5}>
              {selectedEvents.map((event) => (
                <Box
                  key={event.id}
                  onClick={() => onEventClick?.(event)}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: onEventClick ? 'pointer' : 'default',
                    borderStyle: event.isFuture ? 'dashed' : 'solid',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                    <Chip
                      size="small"
                      label={TIMELINE_CATEGORY_LABELS[event.category]}
                      sx={{
                        bgcolor: TIMELINE_CATEGORY_DOT_COLORS[event.category],
                        color: '#fff',
                      }}
                    />
                    {event.isFuture && (
                      <Chip size="small" variant="outlined" label="À venir" />
                    )}
                  </Stack>
                  <Typography variant="body2" fontWeight={600}>
                    {event.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatTimelineDateTime(event.startAt)}
                  </Typography>
                  {event.description && (
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      {event.description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default StudentParcoursCalendar;
